/**
 * Games Management Screen
 * Admin screen for managing games, challenges, quizzes, and speurtochten
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { useChallenges, ChallengeCard, CreatePhotoChallengeModal, CreateOpdrachtModal } from "@/modules/challenges";
import { useGameRegistry, GameWithConfig, WeekendGameConfig } from "@/modules/games";
import { supabase } from "@/lib/supabase/client";
import { SudokuSettingsModal, SudokuWeekendConfig, DEFAULT_WEEKEND_CONFIG } from "@/modules/sudoku";
import { RunnerWeekendConfig, DEFAULT_RUNNER_WEEKEND_CONFIG } from "@/modules/endless-runner";
import { Match3WeekendConfig, DEFAULT_MATCH3_WEEKEND_CONFIG, DEFAULT_MATCH3_GAME_CONFIG, Match3TimeBonusConfig } from "@/modules/match3";

/**
 * Game type card component
 */
interface GameTypeCardProps {
  icon: string;
  title: string;
  description: string;
  count: number;
  color: string;
  onPress: () => void;
}

const GameTypeCard: React.FC<GameTypeCardProps> = ({
  icon,
  title,
  description,
  count,
  color,
  onPress,
}) => {
  const colors = useThemeColors();

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="rounded-2xl p-4 mb-3"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
    >
      <View className="flex-row items-center">
        <View
          className="w-14 h-14 rounded-xl items-center justify-center mr-4"
          style={{ backgroundColor: `${color}15` }}
        >
          <Ionicons name={icon as any} size={28} color={color} />
        </View>
        <View className="flex-1">
          <Text className="font-bold text-lg" style={{ color: colors.text }}>
            {title}
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            {description}
          </Text>
        </View>
        <View className="items-end">
          <View
            className="px-3 py-1 rounded-full mb-1"
            style={{ backgroundColor: `${color}15` }}
          >
            <Text className="font-bold" style={{ color }}>
              {count}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
        </View>
      </View>
    </TouchableOpacity>
  );
};

/**
 * Games Management Screen
 */
export default function GamesManagementScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, roles } = useAuth();
  const { challenges, isLoading, refetch } = useChallenges();
  const { 
    gamesWithConfig, 
    weekendConfigs, 
    isLoading: isLoadingGames, 
    refetch: refetchGames,
    isCompetitionActive,
  } = useGameRegistry();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreatePhotoModal, setShowCreatePhotoModal] = useState(false);
  const [showCreateOpdrachtModal, setShowCreateOpdrachtModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<"all" | "photo" | "quiz" | "location_checkin" | "opdracht">("all");
  const [showTypeListModal, setShowTypeListModal] = useState(false);
  const [selectedTypeForList, setSelectedTypeForList] = useState<string | null>(null);
  const [savingGame, setSavingGame] = useState<string | null>(null);
  const [showGameSettingsModal, setShowGameSettingsModal] = useState(false);
  const [selectedGameForSettings, setSelectedGameForSettings] = useState<GameWithConfig | null>(null);
  const [gamePointsConfig, setGamePointsConfig] = useState<{
    win: number;
    draw: number;
    participation: number;
  }>({ win: 50, draw: 15, participation: 5 });
  
  // Sudoku specific settings
  const [showSudokuSettingsModal, setShowSudokuSettingsModal] = useState(false);
  const [sudokuConfig, setSudokuConfig] = useState<SudokuWeekendConfig>(DEFAULT_WEEKEND_CONFIG);

  // Endless Runner specific settings
  const [showRunnerSettingsModal, setShowRunnerSettingsModal] = useState(false);
  const [runnerConfig, setRunnerConfig] = useState<RunnerWeekendConfig>(DEFAULT_RUNNER_WEEKEND_CONFIG);

  // Match3 specific settings
  const [showMatch3SettingsModal, setShowMatch3SettingsModal] = useState(false);
  const [match3Config, setMatch3Config] = useState<Match3WeekendConfig>(DEFAULT_MATCH3_WEEKEND_CONFIG);

  // Speurtocht specific settings
  const [showSpeurtochtSettingsModal, setShowSpeurtochtSettingsModal] = useState(false);
  const [speurtochtPointsConfig, setSpeurtochtPointsConfig] = useState({
    item_found: 20,
    all_items_bonus: 100,
    first_team_bonus: 50,
  });

  // State for quizzes count and data
  const [quizzesCount, setQuizzesCount] = useState(0);
  const [weekendEnabledQuizzes, setWeekendEnabledQuizzes] = useState<any[]>([]);
  const [speurtochtenCount, setSpeurtochtenCount] = useState(0);

  /**
   * Fetch quizzes count and weekend-enabled quizzes
   */
  const fetchQuizzesData = useCallback(async () => {
    // Count total quizzes
    const { count, error: countError } = await supabase
      .from("quizzes")
      .select("*", { count: "exact", head: true })
      .eq("is_active", true);

    if (!countError && count !== null) {
      setQuizzesCount(count);
    }

    // Fetch weekend-enabled quizzes with question counts
    const { data: quizzes, error: quizzesError } = await supabase
      .from("quizzes")
      .select(`
        id,
        name,
        icon,
        color,
        question_count,
        points_per_correct,
        bonus_speed,
        is_weekend_enabled
      `)
      .eq("is_active", true)
      .eq("is_weekend_enabled", true);

    if (!quizzesError && quizzes) {
      setWeekendEnabledQuizzes(quizzes);
    }

    // Count speurtochten for active weekend
    if (activeWeekend?.id) {
      const { count: speurtCount } = await supabase
        .from("speurtochts")
        .select("*", { count: "exact", head: true })
        .eq("weekend_id", activeWeekend.id);

      setSpeurtochtenCount(speurtCount || 0);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchQuizzesData();
  }, [fetchQuizzesData]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([refetch(), refetchGames(), fetchQuizzesData()]);
    setIsRefreshing(false);
  }, [refetch, refetchGames, fetchQuizzesData]);

  /**
   * Toggle a game's enabled status for the weekend
   */
  const handleToggleGame = async (game: GameWithConfig) => {
    if (!activeWeekend?.id) return;

    setSavingGame(game.slug);

    try {
      const existingConfig = weekendConfigs.find(c => c.game_slug === game.slug);
      
      if (existingConfig) {
        // Update existing config
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            is_enabled: !existingConfig.is_enabled,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingConfig.id);

        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: game.slug,
            is_enabled: true,
            points_config: game.default_points_config,
          });

        if (error) throw error;
      }

      await refetchGames();
    } catch (error) {
      console.error("Error toggling game:", error);
      Alert.alert("Fout", "Kon spel niet bijwerken");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Open game settings modal
   */
  const handleOpenGameSettings = (game: GameWithConfig) => {
    setSelectedGameForSettings(game);
    
    // Handle Quiz - navigate to quiz management instead of showing modal
    if (game.slug === "quiz") {
      router.push("/(admin)/games/quiz");
      return;
    }

    // Handle Memory - navigate to memory settings (images + points)
    if (game.slug === "puzzle_matching") {
      router.push("/(admin)/games/memory");
      return;
    }

    // Handle Speurtocht with correct point fields
    if (game.slug === "speurtocht") {
      const config = weekendConfigs.find(c => c.game_slug === "speurtocht");
      const pc = config?.points_config as Record<string, number> | null;
      setSpeurtochtPointsConfig({
        item_found: pc?.item_found ?? 20,
        all_items_bonus: pc?.all_items_bonus ?? 100,
        first_team_bonus: pc?.first_team_bonus ?? 50,
      });
      setShowSpeurtochtSettingsModal(true);
      return;
    }

    // Handle Sudoku specially
    if (game.slug === "sudoku") {
      const config = weekendConfigs.find(c => c.game_slug === "sudoku");
      const pointsConfig = config?.points_config as Record<string, number> | null;
      const customConfig = config?.custom_config as Partial<SudokuWeekendConfig> | null;
      
      setSudokuConfig({
        ...DEFAULT_WEEKEND_CONFIG,
        ...customConfig,
        points_per_difficulty: {
          easy: pointsConfig?.easy ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.easy,
          medium: pointsConfig?.medium ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.medium,
          hard: pointsConfig?.hard ?? DEFAULT_WEEKEND_CONFIG.points_per_difficulty.hard,
        },
        perfect_bonus: pointsConfig?.perfect_bonus ?? DEFAULT_WEEKEND_CONFIG.perfect_bonus,
      });
      setShowSudokuSettingsModal(true);
      return;
    }

    // Handle Endless Runner specially
    if (game.slug === "endless_runner") {
      const config = weekendConfigs.find(c => c.game_slug === "endless_runner");
      const pointsConfig = config?.points_config as Record<string, number> | null;
      const customConfig = config?.custom_config as Partial<RunnerWeekendConfig> | null;
      
      setRunnerConfig({
        ...DEFAULT_RUNNER_WEEKEND_CONFIG,
        ...customConfig,
        points_new_highscore: pointsConfig?.points_new_highscore ?? DEFAULT_RUNNER_WEEKEND_CONFIG.points_new_highscore,
        points_participation: pointsConfig?.points_participation ?? DEFAULT_RUNNER_WEEKEND_CONFIG.points_participation,
      });
      setShowRunnerSettingsModal(true);
      return;
    }

    // Handle Match3 specially
    if (game.slug === "match_3") {
      const config = weekendConfigs.find(c => c.game_slug === "match_3");
      const pointsConfig = config?.points_config as Partial<Match3WeekendConfig> | null;
      const customConfig = config?.custom_config as Partial<Match3WeekendConfig> | null;
      
      // Merge time_bonuses from custom_config or use defaults
      const timeBonuses = customConfig?.time_bonuses || [...DEFAULT_MATCH3_GAME_CONFIG.time_bonuses];
      
      setMatch3Config({
        ...DEFAULT_MATCH3_WEEKEND_CONFIG,
        ...pointsConfig,
        ...customConfig,
        time_bonuses: timeBonuses,
      });
      setShowMatch3SettingsModal(true);
      return;
    }
    
    const config = weekendConfigs.find(c => c.game_slug === game.slug);
    const pointsConfig = config?.points_config as { win?: number; draw?: number; participation?: number } || {};
    setGamePointsConfig({
      win: pointsConfig.win ?? 50,
      draw: pointsConfig.draw ?? 15,
      participation: pointsConfig.participation ?? 5,
    });
    setShowGameSettingsModal(true);
  };

  /**
   * Save Sudoku settings
   */
  const handleSaveSudokuSettings = async (newConfig: SudokuWeekendConfig) => {
    if (!activeWeekend?.id) return;

    setSavingGame("sudoku");

    try {
      const config = weekendConfigs.find(c => c.game_slug === "sudoku");
      
      const pointsConfig = {
        easy: newConfig.points_per_difficulty.easy,
        medium: newConfig.points_per_difficulty.medium,
        hard: newConfig.points_per_difficulty.hard,
        perfect_bonus: newConfig.perfect_bonus,
      };

      const customConfig = {
        max_plays_per_day: newConfig.max_plays_per_day,
        allow_unlimited_plays: newConfig.allow_unlimited_plays,
      };

      if (config) {
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            points_config: pointsConfig,
            custom_config: customConfig,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: "sudoku",
            is_enabled: true,
            points_config: pointsConfig,
            custom_config: customConfig,
          });

        if (error) throw error;
      }

      await refetchGames();
      setShowSudokuSettingsModal(false);
      Alert.alert("Opgeslagen", "Sudoku instellingen opgeslagen");
    } catch (error) {
      console.error("Error saving Sudoku settings:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Save Endless Runner settings
   */
  const handleSaveRunnerSettings = async () => {
    if (!selectedGameForSettings || !activeWeekend?.id) return;

    setSavingGame("endless_runner");

    try {
      const config = weekendConfigs.find(c => c.game_slug === "endless_runner");

      const pointsConfig = {
        points_new_highscore: runnerConfig.points_new_highscore,
        points_participation: runnerConfig.points_participation,
      };

      const customConfig = {
        max_plays_per_day: runnerConfig.max_plays_per_day,
        allow_unlimited_plays: runnerConfig.allow_unlimited_plays,
      };

      if (config) {
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            points_config: pointsConfig,
            custom_config: customConfig,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: "endless_runner",
            is_enabled: true,
            points_config: pointsConfig,
            custom_config: customConfig,
          });

        if (error) throw error;
      }

      await refetchGames();
      setShowRunnerSettingsModal(false);
      Alert.alert("Opgeslagen", "Endless Runner instellingen opgeslagen");
    } catch (error) {
      console.error("Error saving Runner settings:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Save Match3 settings
   */
  const handleSaveMatch3Settings = async () => {
    if (!selectedGameForSettings || !activeWeekend?.id) return;

    setSavingGame("match_3");

    try {
      const config = weekendConfigs.find(c => c.game_slug === "match_3");

      const pointsConfig = {
        points_participation: match3Config.points_participation,
        points_new_highscore: match3Config.points_new_highscore,
        points_score_500: match3Config.points_score_500,
        points_score_1000: match3Config.points_score_1000,
        points_score_2000: match3Config.points_score_2000,
        points_score_5000: match3Config.points_score_5000,
      };

      const customConfig = {
        max_plays_per_day: match3Config.max_plays_per_day,
        allow_unlimited_plays: match3Config.allow_unlimited_plays,
        time_bonuses: match3Config.time_bonuses,
      };

      if (config) {
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            points_config: pointsConfig,
            custom_config: customConfig,
            is_enabled: true,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: "match_3",
            is_enabled: true,
            points_config: pointsConfig,
            custom_config: customConfig,
          });

        if (error) throw error;
      }

      await refetchGames();
      setShowMatch3SettingsModal(false);
      Alert.alert("Opgeslagen", "Match 3 instellingen opgeslagen");
    } catch (error) {
      console.error("Error saving Match3 settings:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Save game points configuration
   */
  const handleSaveGameSettings = async () => {
    if (!selectedGameForSettings || !activeWeekend?.id) return;

    setSavingGame(selectedGameForSettings.slug);

    try {
      const config = weekendConfigs.find(c => c.game_slug === selectedGameForSettings.slug);

      if (config) {
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            points_config: gamePointsConfig,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: selectedGameForSettings.slug,
            is_enabled: true,
            points_config: gamePointsConfig,
          });

        if (error) throw error;
      }

      await refetchGames();
      setShowGameSettingsModal(false);
      Alert.alert("Opgeslagen", "Punten configuratie opgeslagen");
    } catch (error) {
      console.error("Error saving game settings:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Save Speurtocht points configuration
   */
  const handleSaveSpeurtochtSettings = async () => {
    if (!activeWeekend?.id) return;

    setSavingGame("speurtocht");

    try {
      const config = weekendConfigs.find(c => c.game_slug === "speurtocht");

      if (config) {
        const { error } = await supabase
          .from("weekend_game_config")
          .update({
            points_config: speurtochtPointsConfig,
            updated_at: new Date().toISOString(),
          })
          .eq("id", config.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("weekend_game_config")
          .insert({
            weekend_id: activeWeekend.id,
            game_slug: "speurtocht",
            is_enabled: true,
            points_config: speurtochtPointsConfig,
          });

        if (error) throw error;
      }

      await refetchGames();
      setShowSpeurtochtSettingsModal(false);
      Alert.alert("Opgeslagen", "Speurtocht punten opgeslagen");
    } catch (error) {
      console.error("Error saving speurtocht settings:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setSavingGame(null);
    }
  };

  /**
   * Count challenges by type
   */
  const getChallengeCount = (type?: string) => {
    if (!type || type === "all") return challenges.length;
    return challenges.filter((c) => c.challenge_type === type).length;
  };

  /**
   * Filter challenges by type
   */
  const filteredChallenges = selectedFilter === "all"
    ? challenges
    : challenges.filter((c) => c.challenge_type === selectedFilter);

  /**
   * Get challenges for the type list modal
   */
  const typeListChallenges = selectedTypeForList
    ? challenges.filter((c) => c.challenge_type === selectedTypeForList)
    : [];

  /**
   * Handle opening the type list modal
   */
  const handleOpenTypeList = (typeId: string) => {
    setSelectedTypeForList(typeId);
    setShowTypeListModal(true);
  };

  /**
   * Game types configuration
   */
  const gameTypes = [
    {
      id: "photo",
      icon: "camera",
      title: "Foto opdrachten",
      description: "Upload uitdagingen",
      color: "#8B5CF6",
      count: getChallengeCount("photo"),
      onPress: () => handleOpenTypeList("photo"),
    },
    {
      id: "quiz",
      icon: "help-circle",
      title: "Quizzen",
      description: "Kennis testen",
      color: "#3B82F6",
      count: quizzesCount,
      onPress: () => router.push("/(admin)/games/quiz"),
    },
    {
      id: "speurtocht",
      icon: "map",
      title: "Speurtochten",
      description: "Schatjacht met stappen",
      color: "#10B981",
      count: speurtochtenCount,
      onPress: () => router.push("/(admin)/games/speurtocht"),
    },
    {
      id: "opdracht",
      icon: "list",
      title: "Opdrachten",
      description: "Multi-sectie uitdagingen",
      color: "#6366F1",
      count: getChallengeCount("opdracht"),
      onPress: () => handleOpenTypeList("opdracht"),
    },
  ];

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="w-10 h-10 items-center justify-center"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text className="font-bold text-lg" style={{ color: colors.text }}>
          Spellen beheren
        </Text>
        <View className="flex-row items-center">
          {/* Settings button for super admins */}
          {roles.isSuperAdmin && (
            <TouchableOpacity
              onPress={() => router.push("/(admin)/games/settings")}
              className="w-10 h-10 items-center justify-center mr-2"
              accessibilityLabel="Spellen instellingen"
            >
              <Ionicons name="settings-outline" size={22} color="#DC2626" />
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={() => setShowCreateModal(true)}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary }}
            accessibilityLabel="Nieuw aanmaken"
          >
            <Ionicons name="add" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Weekend info */}
        {activeWeekend && (
          <View className="px-4 py-4">
            <View
              className="rounded-xl p-4 flex-row items-center justify-between"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <View className="flex-row items-center">
                <Ionicons name="calendar" size={20} color={colors.primary} />
                <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
                  {activeWeekend.name}
                </Text>
              </View>
              {isCompetitionActive && (
                <View className="flex-row items-center px-2 py-1 rounded-full" style={{ backgroundColor: "#10B98120" }}>
                  <Ionicons name="checkmark-circle" size={14} color="#10B981" />
                  <Text className="ml-1 text-xs font-medium" style={{ color: "#10B981" }}>
                    Competitie Actief
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Weekend Games Section */}
        <View className="px-4 mb-4">
          <View className="flex-row items-center justify-between mb-3">
            <View className="flex-row items-center">
              <Ionicons name="trophy" size={18} color="#8B5CF6" />
              <Text className="font-bold text-lg ml-2" style={{ color: colors.text }}>
                Weekend Spellen
              </Text>
            </View>
            <TouchableOpacity 
              onPress={() => router.push("/(admin)/algemeen")}
              className="flex-row items-center"
            >
              <Text className="text-sm mr-1" style={{ color: colors.primary }}>
                Instellingen
              </Text>
              <Ionicons name="settings-outline" size={16} color={colors.primary} />
            </TouchableOpacity>
          </View>
          
          <Text className="text-sm mb-3" style={{ color: colors.textSecondary }}>
            Selecteer welke spellen beschikbaar zijn voor het weekend.
          </Text>

          {isLoadingGames ? (
            <View className="py-6 items-center">
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : gamesWithConfig.filter(g => g.supports_weekend).length === 0 ? (
            <View
              className="p-4 rounded-xl items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Ionicons name="game-controller-outline" size={32} color={colors.textTertiary} />
              <Text className="mt-2" style={{ color: colors.textSecondary }}>
                Geen spellen beschikbaar
              </Text>
            </View>
          ) : (
            <View>
              {gamesWithConfig
                .filter(g => g.supports_weekend)
                .map((game) => {
                  const config = weekendConfigs.find(c => c.game_slug === game.slug);
                  const isEnabled = config?.is_enabled || false;
                  const isSaving = savingGame === game.slug;

                  return (
                    <View
                      key={game.slug}
                      className="rounded-xl p-4 mb-3"
                      style={{
                        backgroundColor: isEnabled ? `${game.color}10` : colors.surface,
                        borderWidth: 2,
                        borderColor: isEnabled ? game.color : colors.border,
                      }}
                    >
                      <View className="flex-row items-center">
                        {/* Game Icon */}
                        <View
                          className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                          style={{ backgroundColor: `${game.color}20` }}
                        >
                          <Ionicons name={game.icon as any} size={24} color={game.color} />
                        </View>

                        {/* Game Info */}
                        <View className="flex-1">
                          <Text className="font-semibold text-base" style={{ color: colors.text }}>
                            {game.name}
                          </Text>
                          <Text className="text-sm" style={{ color: colors.textSecondary }}>
                            {game.description}
                          </Text>
                          {isEnabled && (
                            <View className="flex-row items-center mt-1">
                              <Ionicons name="star" size={12} color="#F59E0B" />
                              <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                                {game.slug === "quiz" && weekendEnabledQuizzes.length > 0
                                  ? `${weekendEnabledQuizzes.reduce((sum, q) => sum + (q.question_count * (q.points_per_correct + q.bonus_speed)), 0)} max punten`
                                  : `Win: ${game.effective_points_config.win || 0} | Gelijk: ${game.effective_points_config.draw || 0}`}
                              </Text>
                            </View>
                          )}
                        </View>

                        {/* Settings Button (only when enabled) */}
                        {isEnabled && (
                          <TouchableOpacity
                            onPress={() => handleOpenGameSettings(game)}
                            className="p-2 mr-2"
                          >
                            <Ionicons name="settings-outline" size={22} color={colors.textSecondary} />
                          </TouchableOpacity>
                        )}

                        {/* Toggle */}
                        <TouchableOpacity
                          onPress={() => handleToggleGame(game)}
                          disabled={isSaving}
                          className="ml-2"
                        >
                          {isSaving ? (
                            <ActivityIndicator color={game.color} size="small" />
                          ) : (
                            <View
                              className="w-14 h-8 rounded-full justify-center px-1"
                              style={{ backgroundColor: isEnabled ? game.color : colors.border }}
                            >
                              <View
                                className="w-6 h-6 rounded-full bg-white items-center justify-center"
                                style={{ alignSelf: isEnabled ? "flex-end" : "flex-start" }}
                              >
                                {isEnabled && (
                                  <Ionicons name="checkmark" size={14} color={game.color} />
                                )}
                              </View>
                            </View>
                          )}
                        </TouchableOpacity>
                      </View>

                      {/* Sudoku specific info */}
                      {game.slug === "sudoku" && isEnabled && (
                        <View className="mt-2 flex-row flex-wrap gap-2">
                          {["easy", "medium", "hard"].map((diff) => {
                            const sudokuPointsConfig = config?.points_config as Record<string, number> | undefined;
                            const points = sudokuPointsConfig?.[diff] || 0;
                            const diffColors: Record<string, string> = { easy: "#10B981", medium: "#F59E0B", hard: "#EF4444" };
                            return (
                              <View key={diff} className="flex-row items-center px-2 py-1 rounded-full" style={{ backgroundColor: `${diffColors[diff]}15` }}>
                                <Text className="text-xs font-medium" style={{ color: diffColors[diff] }}>
                                  {diff === "easy" ? "Makkelijk" : diff === "medium" ? "Normaal" : "Moeilijk"}: {points}pt
                                </Text>
                              </View>
                            );
                          })}
                        </View>
                      )}

                    </View>
                  );
                })}
            </View>
          )}
        </View>

        {/* Divider */}
        <View className="h-px mx-4 mb-4" style={{ backgroundColor: colors.border }} />

        {/* Quick Stats */}
        <View className="px-4 mb-4">
          <View className="flex-row">
            <View
              className="flex-1 mr-2 rounded-xl p-4 items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="text-3xl font-bold" style={{ color: colors.primary }}>
                {challenges.length + weekendEnabledQuizzes.length + gamesWithConfig.filter(g => g.is_weekend_mode && g.slug !== "quiz").length}
              </Text>
              <Text className="text-sm text-center" style={{ color: colors.textSecondary }}>
                Totaal spellen
              </Text>
            </View>
            <View
              className="flex-1 mx-2 rounded-xl p-4 items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="text-3xl font-bold" style={{ color: "#10B981" }}>
                {challenges.filter((c) => c.status === "active").length + weekendEnabledQuizzes.length}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Actief
              </Text>
            </View>
            <View
              className="flex-1 ml-2 rounded-xl p-4 items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <Text className="text-3xl font-bold" style={{ color: "#F59E0B" }}>
                {challenges.filter((c) => c.status === "draft").length}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                Concept
              </Text>
            </View>
          </View>
        </View>

        {/* Game Types */}
        <View className="px-4 mb-4">
          <Text className="font-bold text-lg mb-3" style={{ color: colors.text }}>
            Spel types
          </Text>

          {gameTypes.map((type) => (
            <GameTypeCard
              key={type.id}
              icon={type.icon}
              title={type.title}
              description={type.description}
              count={type.count}
              color={type.color}
              onPress={type.onPress}
            />
          ))}

          {/* Weekend Enabled Quizzes */}
          {weekendEnabledQuizzes.length > 0 && (
            <View className="mt-4">
              <View className="flex-row items-center mb-3">
                <Ionicons name="trophy" size={16} color="#8B5CF6" />
                <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                  Weekend Actieve Quizzen
                </Text>
              </View>
              {weekendEnabledQuizzes.map((quiz) => {
                const maxPoints = quiz.question_count * (quiz.points_per_correct + quiz.bonus_speed);
                return (
                  <TouchableOpacity
                    key={quiz.id}
                    onPress={() => router.push(`/(admin)/games/quiz/${quiz.id}`)}
                    className="rounded-xl p-3 mb-2 flex-row items-center"
                    style={{
                      backgroundColor: `${quiz.color || "#3B82F6"}10`,
                      borderWidth: 1,
                      borderColor: `${quiz.color || "#3B82F6"}30`,
                    }}
                  >
                    <View
                      className="w-10 h-10 rounded-lg items-center justify-center"
                      style={{ backgroundColor: `${quiz.color || "#3B82F6"}20` }}
                    >
                      <Ionicons name={(quiz.icon || "help-circle") as any} size={20} color={quiz.color || "#3B82F6"} />
                    </View>
                    <View className="flex-1 ml-3">
                      <Text className="font-medium" style={{ color: colors.text }}>
                        {quiz.name}
                      </Text>
                      <View className="flex-row items-center mt-1">
                        <Text className="text-xs mr-2" style={{ color: colors.textSecondary }}>
                          {quiz.question_count} vragen
                        </Text>
                        <View className="flex-row items-center">
                          <Ionicons name="star" size={10} color="#F59E0B" />
                          <Text className="text-xs ml-1" style={{ color: "#F59E0B" }}>
                            max {maxPoints} pts
                          </Text>
                        </View>
                      </View>
                    </View>
                    <View
                      className="px-2 py-1 rounded-full"
                      style={{ backgroundColor: "#10B98120" }}
                    >
                      <Text className="text-xs font-medium" style={{ color: "#10B981" }}>
                        Actief
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>

        {/* Create New Section */}
        <View className="px-4 mb-4">
          <Text className="font-bold text-lg mb-3" style={{ color: colors.text }}>
            Nieuw aanmaken
          </Text>
          
          <View className="flex-row flex-wrap">
            <TouchableOpacity
              onPress={() => setShowCreatePhotoModal(true)}
              className="w-[48%] mr-[2%] mb-3 rounded-xl p-4 items-center"
              style={{ backgroundColor: "#8B5CF620" }}
            >
              <View
                className="w-12 h-12 rounded-full items-center justify-center mb-2"
                style={{ backgroundColor: "#8B5CF630" }}
              >
                <Ionicons name="camera" size={24} color="#8B5CF6" />
              </View>
              <Text className="font-semibold" style={{ color: colors.text }}>
                Foto opdracht
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => router.push("/(admin)/games/quiz")}
              className="w-[48%] mb-3 rounded-xl p-4 items-center"
              style={{ backgroundColor: "#3B82F620" }}
            >
              <View
                className="w-12 h-12 rounded-full items-center justify-center mb-2"
                style={{ backgroundColor: "#3B82F630" }}
              >
                <Ionicons name="help-circle" size={24} color="#3B82F6" />
              </View>
              <Text className="font-semibold" style={{ color: colors.text }}>
                Quiz
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => {
                router.push("/(admin)/games/speurtocht/create");
              }}
              className="w-[48%] mr-[2%] mb-3 rounded-xl p-4 items-center"
              style={{ backgroundColor: "#10B98120" }}
            >
              <View
                className="w-12 h-12 rounded-full items-center justify-center mb-2"
                style={{ backgroundColor: "#10B98130" }}
              >
                <Ionicons name="map" size={24} color="#10B981" />
              </View>
              <Text className="font-semibold" style={{ color: colors.text }}>
                Speurtocht
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setShowCreateOpdrachtModal(true)}
              className="w-[48%] mb-3 rounded-xl p-4 items-center"
              style={{ backgroundColor: "#6366F120" }}
            >
              <View
                className="w-12 h-12 rounded-full items-center justify-center mb-2"
                style={{ backgroundColor: "#6366F130" }}
              >
                <Ionicons name="list" size={24} color="#6366F1" />
              </View>
              <Text className="font-semibold" style={{ color: colors.text }}>
                Opdracht
              </Text>
              <Text className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                Multi-sectie
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Recent/Filtered Challenges */}
        {filteredChallenges.length > 0 && (
          <View className="px-4 mb-4">
            <View className="flex-row items-center justify-between mb-3">
              <Text className="font-bold text-lg" style={{ color: colors.text }}>
                {selectedFilter === "all" ? "Recente spellen" : `${gameTypes.find((t) => t.id === selectedFilter)?.title || "Spellen"}`}
              </Text>
              {selectedFilter !== "all" && (
                <TouchableOpacity onPress={() => setSelectedFilter("all")}>
                  <Text style={{ color: colors.primary }}>Wis filter</Text>
                </TouchableOpacity>
              )}
            </View>

            {filteredChallenges.slice(0, 5).map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                showLinkStatus={true}
                onPress={() => {
                  router.push(`/(admin)/games/${challenge.id}`);
                }}
              />
            ))}

            {filteredChallenges.length > 5 && (
              <TouchableOpacity
                className="py-3 items-center"
                onPress={() => {
                  // TODO: Show all challenges
                }}
              >
                <Text style={{ color: colors.primary }}>
                  Bekijk alle {filteredChallenges.length} spellen
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Empty state */}
        {challenges.length === 0 && !isLoading && (
          <View className="px-4 py-12 items-center">
            <View
              className="w-20 h-20 rounded-full items-center justify-center mb-4"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="game-controller-outline" size={40} color={colors.textTertiary} />
            </View>
            <Text className="font-semibold text-lg text-center" style={{ color: colors.text }}>
              Nog geen spellen
            </Text>
            <Text className="text-center mt-2" style={{ color: colors.textSecondary }}>
              Maak je eerste uitdaging aan om te beginnen!
            </Text>
            <TouchableOpacity
              onPress={() => setShowCreatePhotoModal(true)}
              className="mt-4 px-6 py-3 rounded-full flex-row items-center"
              style={{ backgroundColor: colors.primary }}
            >
              <Ionicons name="add" size={20} color="#FFFFFF" />
              <Text className="ml-2 font-semibold" style={{ color: "#FFFFFF" }}>
                Foto opdracht maken
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Bottom padding */}
        <View className="h-8" />
      </ScrollView>

      {/* Create Photo Challenge Modal */}
      <CreatePhotoChallengeModal
        visible={showCreatePhotoModal}
        onClose={() => setShowCreatePhotoModal(false)}
        onCreated={() => {
          refetch();
        }}
      />

      {/* Create Opdracht Modal */}
      <CreateOpdrachtModal
        visible={showCreateOpdrachtModal}
        onClose={() => setShowCreateOpdrachtModal(false)}
        onSuccess={() => {
          refetch();
        }}
        weekendId={activeWeekend?.id}
      />

      {/* Type List Modal */}
      <Modal
        visible={showTypeListModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowTypeListModal(false)}
      >
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowTypeListModal(false)}>
              <Text style={{ color: colors.primary }}>Sluiten</Text>
            </TouchableOpacity>
            <Text className="font-bold text-lg" style={{ color: colors.text }}>
              {gameTypes.find((t) => t.id === selectedTypeForList)?.title || "Spellen"}
            </Text>
            <View className="w-14" />
          </View>

          {/* List Content */}
          <ScrollView className="flex-1 px-4 pt-4">
            {typeListChallenges.length === 0 ? (
              <View className="py-12 items-center">
                <View
                  className="w-16 h-16 rounded-full items-center justify-center mb-4"
                  style={{ 
                    backgroundColor: (gameTypes.find((t) => t.id === selectedTypeForList)?.color || colors.primary) + "20" 
                  }}
                >
                  <Ionicons
                    name={(gameTypes.find((t) => t.id === selectedTypeForList)?.icon || "game-controller") as any}
                    size={32}
                    color={gameTypes.find((t) => t.id === selectedTypeForList)?.color || colors.primary}
                  />
                </View>
                <Text className="font-semibold text-lg text-center" style={{ color: colors.text }}>
                  Geen {gameTypes.find((t) => t.id === selectedTypeForList)?.title.toLowerCase() || "spellen"} gevonden
                </Text>
                <Text className="text-center mt-2" style={{ color: colors.textSecondary }}>
                  Maak je eerste aan via de knop hieronder
                </Text>
                
                {selectedTypeForList === "photo" && (
                  <TouchableOpacity
                    onPress={() => {
                      setShowTypeListModal(false);
                      setShowCreatePhotoModal(true);
                    }}
                    className="mt-4 px-6 py-3 rounded-full flex-row items-center"
                    style={{ backgroundColor: "#8B5CF6" }}
                  >
                    <Ionicons name="add" size={20} color="#FFFFFF" />
                    <Text className="ml-2 font-semibold text-white">
                      Foto opdracht maken
                    </Text>
                  </TouchableOpacity>
                )}
                {selectedTypeForList === "opdracht" && (
                  <TouchableOpacity
                    onPress={() => {
                      setShowTypeListModal(false);
                      setShowCreateOpdrachtModal(true);
                    }}
                    className="mt-4 px-6 py-3 rounded-full flex-row items-center"
                    style={{ backgroundColor: "#6366F1" }}
                  >
                    <Ionicons name="add" size={20} color="#FFFFFF" />
                    <Text className="ml-2 font-semibold text-white">
                      Opdracht maken
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              <>
                {/* Stats bar */}
                <View 
                  className="flex-row rounded-xl p-3 mb-4"
                  style={{ 
                    backgroundColor: (gameTypes.find((t) => t.id === selectedTypeForList)?.color || colors.primary) + "15" 
                  }}
                >
                  <View className="flex-1 items-center">
                    <Text 
                      className="text-xl font-bold"
                      style={{ color: gameTypes.find((t) => t.id === selectedTypeForList)?.color || colors.primary }}
                    >
                      {typeListChallenges.length}
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>Totaal</Text>
                  </View>
                  <View className="flex-1 items-center">
                    <Text className="text-xl font-bold" style={{ color: "#10B981" }}>
                      {typeListChallenges.filter((c) => c.status === "active").length}
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>Actief</Text>
                  </View>
                  <View className="flex-1 items-center">
                    <Text className="text-xl font-bold" style={{ color: "#6B7280" }}>
                      {typeListChallenges.filter((c) => c.status === "draft").length}
                    </Text>
                    <Text className="text-xs" style={{ color: colors.textSecondary }}>Concept</Text>
                  </View>
                </View>

                {/* Challenge list */}
                {typeListChallenges.map((challenge) => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    showLinkStatus={true}
                    onPress={() => {
                      setShowTypeListModal(false);
                      router.push(`/(admin)/games/${challenge.id}`);
                    }}
                  />
                ))}
              </>
            )}

            {/* Bottom padding */}
            <View className="h-8" />
          </ScrollView>

          {/* FAB for creating new challenge of this type */}
          {selectedTypeForList === "photo" && typeListChallenges.length > 0 && (
            <TouchableOpacity
              onPress={() => {
                setShowTypeListModal(false);
                setShowCreatePhotoModal(true);
              }}
              className="absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
              style={{ backgroundColor: "#8B5CF6" }}
            >
              <Ionicons name="add" size={28} color="#FFFFFF" />
            </TouchableOpacity>
          )}
          {selectedTypeForList === "opdracht" && typeListChallenges.length > 0 && (
            <TouchableOpacity
              onPress={() => {
                setShowTypeListModal(false);
                setShowCreateOpdrachtModal(true);
              }}
              className="absolute bottom-6 right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
              style={{ backgroundColor: "#6366F1" }}
            >
              <Ionicons name="add" size={28} color="#FFFFFF" />
            </TouchableOpacity>
          )}
        </SafeAreaView>
      </Modal>

      {/* Create Game Modal */}
      <Modal visible={showCreateModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Header */}
          <View className="px-4 py-4 flex-row items-center justify-between border-b" style={{ borderColor: colors.border }}>
            <TouchableOpacity
              onPress={() => setShowCreateModal(false)}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface }}
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="font-bold text-lg" style={{ color: colors.text }}>
              Nieuw Aanmaken
            </Text>
            <View className="w-10" />
          </View>

          <ScrollView className="flex-1 px-4 pt-6" showsVerticalScrollIndicator={false}>
            <Text className="text-sm mb-4" style={{ color: colors.textSecondary }}>
              Selecteer wat je wilt aanmaken
            </Text>

            {/* Photo Challenge */}
            <TouchableOpacity
              onPress={() => {
                setShowCreateModal(false);
                setShowCreatePhotoModal(true);
              }}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                style={{ backgroundColor: "#8B5CF620" }}
              >
                <Ionicons name="camera" size={28} color="#8B5CF6" />
              </View>
              <View className="flex-1">
                <Text className="font-bold text-base" style={{ color: colors.text }}>
                  Foto Opdracht
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Maak een foto challenge aan
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
            </TouchableOpacity>

            {/* Quiz */}
            <TouchableOpacity
              onPress={() => {
                setShowCreateModal(false);
                router.push("/(admin)/games/quiz");
              }}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                style={{ backgroundColor: "#3B82F620" }}
              >
                <Ionicons name="help-circle" size={28} color="#3B82F6" />
              </View>
              <View className="flex-1">
                <Text className="font-bold text-base" style={{ color: colors.text }}>
                  Quiz
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Maak een quiz met vragen
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
            </TouchableOpacity>

            {/* Speurtocht */}
            <TouchableOpacity
              onPress={() => {
                setShowCreateModal(false);
                router.push("/(admin)/games/speurtocht/create");
              }}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                style={{ backgroundColor: "#10B98120" }}
              >
                <Ionicons name="map" size={28} color="#10B981" />
              </View>
              <View className="flex-1">
                <Text className="font-bold text-base" style={{ color: colors.text }}>
                  Speurtocht
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Speurtocht locaties
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
            </TouchableOpacity>

            {/* Opdracht */}
            <TouchableOpacity
              onPress={() => {
                setShowCreateModal(false);
                setShowCreateOpdrachtModal(true);
              }}
              className="rounded-xl p-4 mb-3 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            >
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mr-4"
                style={{ backgroundColor: "#6366F120" }}
              >
                <Ionicons name="list" size={28} color="#6366F1" />
              </View>
              <View className="flex-1">
                <Text className="font-bold text-base" style={{ color: colors.text }}>
                  Opdracht
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Multi-sectie uitdagingen
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
            </TouchableOpacity>

            <View className="h-12" />
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Game Settings Modal */}
      <Modal
        visible={showGameSettingsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowGameSettingsModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-6">
              <Text style={{ color: colors.text }} className="text-xl font-bold">
                {selectedGameForSettings?.name} Instellingen
              </Text>
              <TouchableOpacity onPress={() => setShowGameSettingsModal(false)}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Points Configuration */}
            <Text style={{ color: colors.textSecondary }} className="mb-4 font-medium">
              Punten Configuratie
            </Text>

            {/* Win Points */}
            <View className="mb-4">
              <Text style={{ color: colors.text }} className="mb-2">
                Punten voor winst
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, win: Math.max(0, prev.win - 5) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={gamePointsConfig.win.toString()}
                  onChangeText={(text) => setGamePointsConfig(prev => ({ ...prev, win: parseInt(text) || 0 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: "#22C55E" }}
                />
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, win: prev.win + 5 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Draw Points */}
            <View className="mb-4">
              <Text style={{ color: colors.text }} className="mb-2">
                Punten voor gelijkspel
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, draw: Math.max(0, prev.draw - 5) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={gamePointsConfig.draw.toString()}
                  onChangeText={(text) => setGamePointsConfig(prev => ({ ...prev, draw: parseInt(text) || 0 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: "#F59E0B" }}
                />
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, draw: prev.draw + 5 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Participation Points */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="mb-2">
                Punten voor deelname (verlies)
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, participation: Math.max(0, prev.participation - 1) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={gamePointsConfig.participation.toString()}
                  onChangeText={(text) => setGamePointsConfig(prev => ({ ...prev, participation: parseInt(text) || 0 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.textSecondary }}
                />
                <TouchableOpacity
                  onPress={() => setGamePointsConfig(prev => ({ ...prev, participation: prev.participation + 1 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Info */}
            <View 
              className="p-3 rounded-xl mb-6 flex-row items-start"
              style={{ backgroundColor: `${colors.primary}15` }}
            >
              <Ionicons name="information-circle" size={20} color={colors.primary} />
              <Text style={{ color: colors.textSecondary }} className="ml-2 flex-1 text-sm">
                Bij weekend competitie kunnen alleen spelers van verschillende teams elkaar uitdagen.
              </Text>
            </View>

            {/* Action Buttons */}
            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setShowGameSettingsModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">
                  Annuleren
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleSaveGameSettings}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.primary }}
                disabled={savingGame !== null}
              >
                {savingGame ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text className="text-white font-semibold">Opslaan</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Speurtocht Settings Modal */}
      <Modal
        visible={showSpeurtochtSettingsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSpeurtochtSettingsModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-6">
              <Text style={{ color: colors.text }} className="text-xl font-bold">
                Speurtocht Instellingen
              </Text>
              <TouchableOpacity onPress={() => setShowSpeurtochtSettingsModal(false)}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={{ color: colors.textSecondary }} className="mb-4 font-medium">
              Punten Configuratie
            </Text>

            {[
              { key: "item_found" as const, label: "Punten per gevonden item", color: "#10B981", step: 5 },
              { key: "all_items_bonus" as const, label: "Bonus alles gevonden", color: "#F59E0B", step: 10 },
              { key: "first_team_bonus" as const, label: "Bonus eerste team", color: "#8B5CF6", step: 10 },
            ].map((field) => (
              <View key={field.key} className="mb-4">
                <Text style={{ color: colors.text }} className="mb-2">
                  {field.label}
                </Text>
                <View className="flex-row items-center">
                  <TouchableOpacity
                    onPress={() => setSpeurtochtPointsConfig((prev) => ({ ...prev, [field.key]: Math.max(0, prev[field.key] - field.step) }))}
                    className="w-12 h-12 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={24} color={colors.text} />
                  </TouchableOpacity>
                  <TextInput
                    value={speurtochtPointsConfig[field.key].toString()}
                    onChangeText={(text) => setSpeurtochtPointsConfig((prev) => ({ ...prev, [field.key]: parseInt(text) || 0 }))}
                    keyboardType="number-pad"
                    className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                    style={{ backgroundColor: colors.surfaceSecondary, color: field.color }}
                  />
                  <TouchableOpacity
                    onPress={() => setSpeurtochtPointsConfig((prev) => ({ ...prev, [field.key]: prev[field.key] + field.step }))}
                    className="w-12 h-12 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={24} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            <View className="flex-row gap-3 mt-2">
              <TouchableOpacity
                onPress={() => setShowSpeurtochtSettingsModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">Annuleren</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSaveSpeurtochtSettings}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.primary }}
                disabled={savingGame !== null}
              >
                {savingGame === "speurtocht" ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text className="text-white font-semibold">Opslaan</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Sudoku Settings Modal */}
      <SudokuSettingsModal
        visible={showSudokuSettingsModal}
        onClose={() => setShowSudokuSettingsModal(false)}
        onSave={handleSaveSudokuSettings}
        initialConfig={sudokuConfig}
        isSaving={savingGame === "sudoku"}
      />

      {/* Endless Runner Settings Modal */}
      <Modal
        visible={showRunnerSettingsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRunnerSettingsModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <View
            className="w-full rounded-2xl p-6"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-6">
              <Text style={{ color: colors.text }} className="text-xl font-bold">
                Endless Runner Instellingen
              </Text>
              <TouchableOpacity onPress={() => setShowRunnerSettingsModal(false)}>
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Points Configuration */}
            <Text style={{ color: colors.textSecondary }} className="mb-4 font-medium">
              Punten Configuratie
            </Text>

            {/* New Family Record Points */}
            <View className="mb-4">
              <Text style={{ color: colors.text }} className="mb-2">
                Punten voor nieuw familie record
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, points_new_highscore: Math.max(0, prev.points_new_highscore - 5) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={runnerConfig.points_new_highscore.toString()}
                  onChangeText={(text) => setRunnerConfig(prev => ({ ...prev, points_new_highscore: parseInt(text) || 0 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: "#22C55E" }}
                />
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, points_new_highscore: prev.points_new_highscore + 5 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Participation Points */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="mb-2">
                Punten voor deelname
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, points_participation: Math.max(0, prev.points_participation - 1) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={runnerConfig.points_participation.toString()}
                  onChangeText={(text) => setRunnerConfig(prev => ({ ...prev, points_participation: parseInt(text) || 0 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: "#F59E0B" }}
                />
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, points_participation: prev.points_participation + 1 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Max Plays Per Day */}
            <View className="mb-6">
              <Text style={{ color: colors.text }} className="mb-2">
                Maximaal aantal keer spelen per dag
              </Text>
              <View className="flex-row items-center">
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, max_plays_per_day: Math.max(1, prev.max_plays_per_day - 1) }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="remove" size={24} color={colors.text} />
                </TouchableOpacity>
                <TextInput
                  value={runnerConfig.max_plays_per_day.toString()}
                  onChangeText={(text) => setRunnerConfig(prev => ({ ...prev, max_plays_per_day: parseInt(text) || 1 }))}
                  keyboardType="number-pad"
                  className="flex-1 mx-3 py-3 rounded-xl text-center text-xl font-bold"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />
                <TouchableOpacity
                  onPress={() => setRunnerConfig(prev => ({ ...prev, max_plays_per_day: prev.max_plays_per_day + 1 }))}
                  className="w-12 h-12 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="add" size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Info */}
            <View 
              className="p-3 rounded-xl mb-6 flex-row items-start"
              style={{ backgroundColor: `${colors.primary}15` }}
            >
              <Ionicons name="information-circle" size={20} color={colors.primary} />
              <Text style={{ color: colors.textSecondary }} className="ml-2 flex-1 text-sm">
                Spelers krijgen deelname punten bij elk spel, plus bonus punten als ze het beste familie record verslaan.
              </Text>
            </View>

            {/* Action Buttons */}
            <View className="flex-row gap-3">
              <TouchableOpacity
                onPress={() => setShowRunnerSettingsModal(false)}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }} className="font-semibold">
                  Annuleren
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleSaveRunnerSettings}
                className="flex-1 py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.primary }}
                disabled={savingGame !== null}
              >
                {savingGame === "endless_runner" ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <Text className="text-white font-semibold">Opslaan</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Match3 Settings Modal */}
      <Modal
        visible={showMatch3SettingsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMatch3SettingsModal(false)}
      >
        <View className="flex-1 justify-center items-center bg-black/50 px-6">
          <ScrollView 
            className="w-full max-h-[90%]"
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ flexGrow: 1, justifyContent: "center" }}
          >
            <View
              className="rounded-2xl p-6"
              style={{ backgroundColor: colors.surface }}
            >
              <View className="flex-row items-center justify-between mb-6">
                <Text style={{ color: colors.text }} className="text-xl font-bold">
                  Match 3 Instellingen
                </Text>
                <TouchableOpacity onPress={() => setShowMatch3SettingsModal(false)}>
                  <Ionicons name="close" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              {/* Points Configuration */}
              <Text style={{ color: colors.textSecondary }} className="mb-4 font-medium">
                Punten Configuratie
              </Text>

              {/* Participation Points */}
              <View className="mb-4">
                <Text style={{ color: colors.text }} className="mb-2 font-medium">
                  Deelname punten
                </Text>
                <View className="flex-row items-center gap-3">
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, points_participation: Math.max(0, p.points_participation - 5) }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={20} color={colors.text} />
                  </TouchableOpacity>
                  <View className="flex-1 rounded-xl py-3 items-center" style={{ backgroundColor: colors.surfaceSecondary }}>
                    <Text style={{ color: colors.text }} className="font-bold text-lg">
                      {match3Config.points_participation}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, points_participation: p.points_participation + 5 }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={20} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* New Record Bonus */}
              <View className="mb-4">
                <Text style={{ color: colors.text }} className="mb-2 font-medium">
                  Bonus bij nieuw familie record
                </Text>
                <View className="flex-row items-center gap-3">
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, points_new_highscore: Math.max(0, p.points_new_highscore - 5) }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="remove" size={20} color={colors.text} />
                  </TouchableOpacity>
                  <View className="flex-1 rounded-xl py-3 items-center" style={{ backgroundColor: colors.successLight }}>
                    <Text style={{ color: colors.success }} className="font-bold text-lg">
                      +{match3Config.points_new_highscore}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, points_new_highscore: p.points_new_highscore + 5 }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Ionicons name="add" size={20} color={colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Milestone Points */}
              <Text style={{ color: colors.textSecondary }} className="mb-3 font-medium mt-2">
                Mijlpaal Bonussen
              </Text>

              {/* Score 500 */}
              <View className="mb-3 p-3 rounded-xl" style={{ backgroundColor: colors.surfaceSecondary }}>
                <View className="flex-row items-center justify-between">
                  <Text style={{ color: colors.text }}>Score ≥ 500</Text>
                  <View className="flex-row items-center gap-2">
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_500: Math.max(0, p.points_score_500 - 5) }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="remove" size={16} color={colors.text} />
                    </TouchableOpacity>
                    <Text style={{ color: colors.primary }} className="font-bold w-10 text-center">
                      +{match3Config.points_score_500}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_500: p.points_score_500 + 5 }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="add" size={16} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Score 1000 */}
              <View className="mb-3 p-3 rounded-xl" style={{ backgroundColor: colors.surfaceSecondary }}>
                <View className="flex-row items-center justify-between">
                  <Text style={{ color: colors.text }}>Score ≥ 1000</Text>
                  <View className="flex-row items-center gap-2">
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_1000: Math.max(0, p.points_score_1000 - 5) }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="remove" size={16} color={colors.text} />
                    </TouchableOpacity>
                    <Text style={{ color: colors.primary }} className="font-bold w-10 text-center">
                      +{match3Config.points_score_1000}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_1000: p.points_score_1000 + 5 }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="add" size={16} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Score 2000 */}
              <View className="mb-3 p-3 rounded-xl" style={{ backgroundColor: colors.surfaceSecondary }}>
                <View className="flex-row items-center justify-between">
                  <Text style={{ color: colors.text }}>Score ≥ 2000</Text>
                  <View className="flex-row items-center gap-2">
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_2000: Math.max(0, p.points_score_2000 - 5) }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="remove" size={16} color={colors.text} />
                    </TouchableOpacity>
                    <Text style={{ color: colors.primary }} className="font-bold w-10 text-center">
                      +{match3Config.points_score_2000}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_2000: p.points_score_2000 + 5 }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="add" size={16} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Score 5000 */}
              <View className="mb-4 p-3 rounded-xl" style={{ backgroundColor: colors.surfaceSecondary }}>
                <View className="flex-row items-center justify-between">
                  <Text style={{ color: colors.text }}>Score ≥ 5000</Text>
                  <View className="flex-row items-center gap-2">
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_5000: Math.max(0, p.points_score_5000 - 10) }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="remove" size={16} color={colors.text} />
                    </TouchableOpacity>
                    <Text style={{ color: colors.warning }} className="font-bold w-10 text-center">
                      +{match3Config.points_score_5000}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setMatch3Config(p => ({ ...p, points_score_5000: p.points_score_5000 + 10 }))}
                      className="w-8 h-8 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surface }}
                    >
                      <Ionicons name="add" size={16} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Max Plays Per Day */}
              <View className="mb-4">
                <Text style={{ color: colors.text }} className="mb-2 font-medium">
                  Max potjes per dag
                </Text>
                <View className="flex-row items-center gap-3">
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, max_plays_per_day: Math.max(1, p.max_plays_per_day - 1) }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    disabled={match3Config.allow_unlimited_plays}
                  >
                    <Ionicons name="remove" size={20} color={match3Config.allow_unlimited_plays ? colors.textTertiary : colors.text} />
                  </TouchableOpacity>
                  <View className="flex-1 rounded-xl py-3 items-center" style={{ backgroundColor: colors.surfaceSecondary }}>
                    <Text style={{ color: match3Config.allow_unlimited_plays ? colors.textTertiary : colors.text }} className="font-bold text-lg">
                      {match3Config.allow_unlimited_plays ? "∞" : match3Config.max_plays_per_day}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => setMatch3Config(p => ({ ...p, max_plays_per_day: p.max_plays_per_day + 1 }))}
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                    disabled={match3Config.allow_unlimited_plays}
                  >
                    <Ionicons name="add" size={20} color={match3Config.allow_unlimited_plays ? colors.textTertiary : colors.text} />
                  </TouchableOpacity>
                </View>
              </View>

              {/* Time Bonuses Section */}
              <View className="mb-4">
                <View className="flex-row items-center justify-between mb-3">
                  <Text style={{ color: colors.textSecondary }} className="font-medium">
                    Tijd Bonussen
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      const currentBonuses = match3Config.time_bonuses || [];
                      const maxThreshold = currentBonuses.length > 0
                        ? Math.max(...currentBonuses.map(b => b.score_threshold))
                        : 0;
                      const newBonus: Match3TimeBonusConfig = {
                        score_threshold: maxThreshold + 1000,
                        bonus_seconds: 15,
                        label: `+15s bij ${maxThreshold + 1000} punten`,
                      };
                      setMatch3Config(p => ({
                        ...p,
                        time_bonuses: [...(p.time_bonuses || []), newBonus],
                      }));
                    }}
                    className="flex-row items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ backgroundColor: colors.primary + "20" }}
                    accessibilityLabel="Bonus toevoegen"
                    accessibilityRole="button"
                  >
                    <Ionicons name="add-circle" size={14} color={colors.primary} />
                    <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                      Toevoegen
                    </Text>
                  </TouchableOpacity>
                </View>

                {(match3Config.time_bonuses || []).map((bonus, index) => (
                  <View
                    key={index}
                    className="rounded-xl p-3 mb-2"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    {/* Header with bonus number and delete button */}
                    <View className="flex-row items-center justify-between mb-2">
                      <Text className="text-xs font-medium" style={{ color: colors.textTertiary }}>
                        Bonus {index + 1}
                      </Text>
                      <TouchableOpacity
                        onPress={() => {
                          if ((match3Config.time_bonuses?.length || 0) <= 1) {
                            Alert.alert("Fout", "Je moet minimaal 1 tijd bonus hebben");
                            return;
                          }
                          setMatch3Config(p => ({
                            ...p,
                            time_bonuses: (p.time_bonuses || []).filter((_, i) => i !== index),
                          }));
                        }}
                        className="w-6 h-6 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.error + "20" }}
                        accessibilityLabel={`Verwijder bonus ${index + 1}`}
                        accessibilityRole="button"
                      >
                        <Ionicons name="trash-outline" size={12} color={colors.error} />
                      </TouchableOpacity>
                    </View>

                    {/* Score threshold */}
                    <View className="flex-row items-center justify-between mb-2">
                      <Text className="text-xs" style={{ color: colors.textSecondary }}>
                        Score drempel:
                      </Text>
                      <View className="flex-row items-center gap-1">
                        <TouchableOpacity
                          onPress={() => {
                            const newBonuses = [...(match3Config.time_bonuses || [])];
                            newBonuses[index] = { ...bonus, score_threshold: Math.max(100, bonus.score_threshold - 100) };
                            setMatch3Config(p => ({ ...p, time_bonuses: newBonuses }));
                          }}
                          className="w-6 h-6 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="remove" size={12} color={colors.text} />
                        </TouchableOpacity>
                        <Text className="font-medium w-12 text-center text-xs" style={{ color: colors.text }}>
                          {bonus.score_threshold}
                        </Text>
                        <TouchableOpacity
                          onPress={() => {
                            const newBonuses = [...(match3Config.time_bonuses || [])];
                            newBonuses[index] = { ...bonus, score_threshold: bonus.score_threshold + 100 };
                            setMatch3Config(p => ({ ...p, time_bonuses: newBonuses }));
                          }}
                          className="w-6 h-6 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="add" size={12} color={colors.text} />
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Bonus seconds */}
                    <View className="flex-row items-center justify-between">
                      <Text className="text-xs" style={{ color: colors.textSecondary }}>
                        Bonus seconden:
                      </Text>
                      <View className="flex-row items-center gap-1">
                        <TouchableOpacity
                          onPress={() => {
                            const newBonuses = [...(match3Config.time_bonuses || [])];
                            newBonuses[index] = { ...bonus, bonus_seconds: Math.max(5, bonus.bonus_seconds - 5) };
                            setMatch3Config(p => ({ ...p, time_bonuses: newBonuses }));
                          }}
                          className="w-6 h-6 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="remove" size={12} color={colors.text} />
                        </TouchableOpacity>
                        <Text className="font-medium w-10 text-center text-xs" style={{ color: colors.success }}>
                          +{bonus.bonus_seconds}s
                        </Text>
                        <TouchableOpacity
                          onPress={() => {
                            const newBonuses = [...(match3Config.time_bonuses || [])];
                            newBonuses[index] = { ...bonus, bonus_seconds: bonus.bonus_seconds + 5 };
                            setMatch3Config(p => ({ ...p, time_bonuses: newBonuses }));
                          }}
                          className="w-6 h-6 rounded-lg items-center justify-center"
                          style={{ backgroundColor: colors.surface }}
                        >
                          <Ionicons name="add" size={12} color={colors.text} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))}

                {/* Empty state */}
                {(!match3Config.time_bonuses || match3Config.time_bonuses.length === 0) && (
                  <View
                    className="rounded-xl p-4 items-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Geen tijd bonussen
                    </Text>
                  </View>
                )}
              </View>

              {/* Unlimited Toggle */}
              <TouchableOpacity
                onPress={() => setMatch3Config(p => ({ ...p, allow_unlimited_plays: !p.allow_unlimited_plays }))}
                className="flex-row items-center justify-between p-4 rounded-xl mb-6"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.text }} className="font-medium">
                  Onbeperkt spelen
                </Text>
                <View
                  className="w-12 h-7 rounded-full justify-center px-1"
                  style={{
                    backgroundColor: match3Config.allow_unlimited_plays
                      ? colors.success
                      : colors.border,
                  }}
                >
                  <View
                    className="w-5 h-5 rounded-full"
                    style={{
                      backgroundColor: "#FFFFFF",
                      alignSelf: match3Config.allow_unlimited_plays ? "flex-end" : "flex-start",
                    }}
                  />
                </View>
              </TouchableOpacity>

              {/* Info */}
              <View 
                className="p-3 rounded-xl mb-6 flex-row items-start"
                style={{ backgroundColor: `${colors.primary}15` }}
              >
                <Ionicons name="information-circle" size={20} color={colors.primary} />
                <Text style={{ color: colors.textSecondary }} className="ml-2 flex-1 text-sm">
                  Spelers verdienen deelname punten + bonussen voor elke score mijlpaal die ze behalen.
                </Text>
              </View>

              {/* Action Buttons */}
              <View className="flex-row gap-3">
                <TouchableOpacity
                  onPress={() => setShowMatch3SettingsModal(false)}
                  className="flex-1 py-3 rounded-xl items-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text style={{ color: colors.text }} className="font-semibold">
                    Annuleren
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleSaveMatch3Settings}
                  className="flex-1 py-3 rounded-xl items-center"
                  style={{ backgroundColor: colors.primary }}
                  disabled={savingGame !== null}
                >
                  {savingGame === "match_3" ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text className="text-white font-semibold">Opslaan</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

